/**
 * True when the request never got an answer at all -- a timeout or a dead
 * connection, as opposed to a server that replied with an error.
 *
 * The distinction is load-bearing in AuthContext: "the server says you are not
 * signed in" and "the server did not answer" are the same shape to axios (both
 * land in the catch block) but must NOT produce the same result, or a slow API
 * silently logs the user out. See GitHub issue #24.
 */
export function isTransportError(err) {
  return !err?.response && (err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK');
}

/**
 * The structured body of an AI usage quota 429 (GitHub issue #158), or null
 * for anything else. One helper so the four AI panels that read it cannot
 * drift -- each just does `const quota = quotaFromError(err); if (quota)
 * show the modal; else fall through to the existing alert-error banner`.
 * `extractErrorMessage` still renders `err.response.data.detail` (a plain
 * string on a 429 too) with zero change, so the banner is always a correct
 * fallback even before a caller reads this.
 */
export function quotaFromError(err) {
  if (err?.response?.status !== 429) return null;
  return err.response.data?.quota || null;
}

/**
 * The field a FastAPI 422 entry is about, or '' when it names nothing useful.
 *
 * GitHub issue #164: pydantic's `msg` alone is often unattributable -- a
 * required int arriving as null renders as the bare sentence "Input should be
 * a valid integer", which tells a user nothing about WHICH input. `loc` is a
 * path like ["body", "document_id"] or ["query", "days"]; the wrapper segment
 * ("body"/"query"/"path") is noise to a reader, and a numeric segment is a
 * list index, so both are dropped and the last remaining name wins.
 */
function fieldLabel(entry) {
  if (!Array.isArray(entry?.loc)) return '';
  const parts = entry.loc.filter(
    part => typeof part === 'string' && !['body', 'query', 'path', 'header', 'cookie'].includes(part),
  );
  return parts.length ? parts[parts.length - 1] : '';
}

export function extractErrorMessage(err) {
  // Handle Pydantic validation errors
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;

    // If detail is a string, use it directly
    if (typeof detail === 'string') {
      return detail;
    }

    // If detail is an array of validation errors
    if (Array.isArray(detail)) {
      return detail
        .map(e => {
          if (typeof e === 'string') return e;
          if (e.msg) return fieldLabel(e) ? `${fieldLabel(e)}: ${e.msg}` : e.msg;
          if (e.detail) return e.detail;
          return JSON.stringify(e);
        })
        .join('; ');
    }

    // If detail is an object with msg field
    if (detail.msg) return detail.msg;
  }

  // No response at all: say what actually happened rather than leaking axios'
  // own wording. "timeout of 10000ms exceeded" was reaching users verbatim in
  // 22 different error banners and told them nothing they could act on.
  if (err?.code === 'ECONNABORTED') {
    return 'The server took too long to respond. It may be starting up after a '
      + 'period of inactivity -- please try again in a moment.';
  }
  if (err?.code === 'ERR_NETWORK') {
    return "Can't reach the server. Check your connection and try again.";
  }

  // Fallback
  return err.message || 'An error occurred';
}
